// Admin API: the generic endpoint surface lives in @booking/core-ui/admin/api.
// This app wires it to the admin token store + i18n, then composes the gym's
// /gym/* membership endpoints on top of the shared fetchApi, so the rest of
// admin keeps importing `api` etc. from "@/lib/api". Every booking API mounts
// those routes, so they are there whichever line the host serves.
import {
  getAccessToken,
  clearTokens,
  getTokens,
  saveTokens,
  isTokenExpired,
  parseJwtPayload,
  getClientType,
} from "./auth";
import { getTranslations } from "./i18n/context";
import { createAdminApi } from "@booking/core-ui/admin/api";
import { registerAdminRuntime, type StaffLinkSection } from "@booking/core-ui/admin/runtime";
import { bothLines, hasClinic, hasGym } from "./lines";
import type {
  MembershipPlan,
  Entitlement,
  PlanRequest,
  GymReportSummary,
  GymBalanceRow,
} from "@/types";

const core = createAdminApi({
  getAccessToken,
  getTokens,
  saveTokens,
  clearTokens,
  isTokenExpired,
  parseJwtPayload,
  getClientType,
  translate: (key, params) => getTranslations()(key, params),
});

const { fetchApi } = core;

export const { setOnSessionExpired, getValidAccessToken, ensureSessionOrRedirect, getCurrentUser } = core;

export const api = {
  ...core.api,

  // --- Gym membership endpoints (server extraRoutes) ---
  plans: {
    list: () => fetchApi<MembershipPlan[]>("/gym/admin/plans"),
    create: (data: Partial<MembershipPlan>) =>
      fetchApi<MembershipPlan>("/gym/admin/plans", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: number, data: Partial<MembershipPlan>) =>
      fetchApi<MembershipPlan>(`/gym/admin/plans/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      fetchApi<MembershipPlan>(`/gym/admin/plans/${id}`, {
        method: "DELETE",
      }),
  },

  // Which plans a trainer is on the roster for. A gym-only route rather than
  // planIds on PATCH /admin/staff, so the shared staff endpoint clinic and
  // fudousan serve stays exactly as it was — see internal/gym/staffplans.go.
  staffPlans: {
    get: (staffId: number) =>
      fetchApi<{ planIds: number[] }>(`/gym/admin/staff/${staffId}/plans`),
    set: (staffId: number, planIds: number[]) =>
      fetchApi<{ planIds: number[] }>(`/gym/admin/staff/${staffId}/plans`, {
        method: "PUT",
        body: JSON.stringify({ planIds }),
      }),
  },

  entitlements: {
    list: (params?: { userId?: number; status?: string }) => {
      const p: Record<string, string> = {};
      if (params?.userId) p.userId = String(params.userId);
      if (params?.status) p.status = params.status;
      const qs = new URLSearchParams(p).toString();
      return fetchApi<Entitlement[]>(
        `/gym/entitlements${qs ? `?${qs}` : ""}`,
      );
    },
    issue: (data: {
      userId: number;
      planId: number;
      paymentMethod: string;
      amountPaid?: number;
      recordedByStaffId?: number;
      partnerName?: string;
    }) =>
      fetchApi<Entitlement>("/gym/entitlements", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (
      id: number,
      data: { expiresAt?: string | null; remaining?: number; status?: string; partnerName?: string | null },
    ) =>
      fetchApi<Entitlement>(`/gym/entitlements/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    delete: (id: number) =>
      fetchApi<{ id: number }>(`/gym/entitlements/${id}`, {
        method: "DELETE",
      }),
  },

  planRequests: {
    list: (params?: { status?: string }) => {
      const p: Record<string, string> = {};
      if (params?.status) p.status = params.status;
      const qs = new URLSearchParams(p).toString();
      return fetchApi<PlanRequest[]>(
        `/gym/plan-requests${qs ? `?${qs}` : ""}`,
      );
    },
    fulfill: (
      id: number,
      data: {
        paymentMethod: string;
        amountPaid?: number;
        recordedByStaffId?: number;
        partnerName?: string | null;
      },
    ) =>
      fetchApi<Entitlement>(`/gym/plan-requests/${id}/fulfill`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    cancel: (id: number) =>
      fetchApi<PlanRequest>(`/gym/plan-requests/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "CANCELLED" }),
      }),
    delete: (id: number) =>
      fetchApi<{ id: number }>(`/gym/plan-requests/${id}`, {
        method: "DELETE",
      }),
  },

  gymReports: {
    summary: (params?: { from?: string; to?: string }) => {
      const p: Record<string, string> = {};
      if (params?.from) p.from = params.from;
      if (params?.to) p.to = params.to;
      const qs = new URLSearchParams(p).toString();
      return fetchApi<GymReportSummary>(
        `/gym/admin/reports/summary${qs ? `?${qs}` : ""}`,
      );
    },
    balances: () =>
      fetchApi<GymBalanceRow[]>("/gym/admin/reports/balances"),
  },
};

/*
  担当サービス: no request to read the selection -- the staff payload already
  embeds `services`. The save is a second PATCH carrying only serviceIds, which
  is safe because UpdateStaff COALESCEs every column it was not given.
*/
const serviceLinks: StaffLinkSection = {
  loadOptions: async () => (await api.services.list()).map((s) => ({ id: s.id, name: s.name })),
  loadSelection: async (staff) => (staff.services ?? []).map((sv) => sv.service.id),
  save: async (staffId, serviceIds) => {
    await api.staff.update(staffId, { serviceIds });
  },
};

// 担当プラン: a gym-only route, not planIds on PATCH /admin/staff, so the shared
// staff endpoint stays as it was -- see internal/gym/staffplans.go.
const planLinks: StaffLinkSection = {
  labelKey: "staffPlanLinks",
  loadOptions: async () =>
    (await api.plans.list()).filter((p) => p.active).map((p) => ({ id: p.id, name: p.name })),
  loadSelection: async (staff) => (await api.staffPlans.get(staff.id)).planIds,
  save: async (staffId, planIds) => {
    await api.staffPlans.set(staffId, planIds);
  },
};

// Inject the app runtime so shared admin components/pages can reach the api.
registerAdminRuntime({
  api,
  getValidAccessToken,
  ensureSessionOrRedirect,
  getCurrentUser,
  setOnSessionExpired,
  clearTokens,
  getIdToken: () => getTokens()?.id_token ?? null,

  /*
    The staff edit modal's link sections, one per line served: 担当サービス
    (staff_services, the clinic's) and 担当プラン (staff_plans, the gym's).
    Serving both, the modal shows both; serving one, only that one.

    Only active plans are offered. A deactivated plan is hidden from the catalog
    rather than deleted (see the DELETE note in internal/gym/admin.go): a trainer
    already linked to one keeps the row, it is simply not a box any more.
  */
  staffLinks: [
    ...(hasClinic ? [serviceLinks] : []),
    ...(hasGym ? [planLinks] : []),
  ],
  // One company, two lines: service kinds and the special's scope (core-ui).
  ...(bothLines
    ? {
        productLines: {
          plans: async () =>
            (await api.plans.list()).filter((p) => p.active).map((p) => ({ id: p.id, name: p.name })),
        },
      }
    : {}),
});
